import { useQuery } from '@tanstack/react-query'
import { Users, Sparkles, TrendingUp, AlertCircle, Coins } from 'lucide-react'
import { useAppStore } from '../../store/useAppStore'
import { adminApi } from './adminApi'

interface DashboardData {
  total_users: number
  active_today: number
  total_dreams: number
  dreams_today: number
  total_transactions: number
  total_revenue_baht: number
  pending_review: number
  total_admins: number
  daily_dreams_7days: { date: string; count: number }[]
}

export default function AdminDashboardPage() {
  const { lineUid, profile } = useAppStore()

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'dashboard', lineUid],
    queryFn: () => adminApi.dashboard(lineUid!) as Promise<DashboardData>,
    enabled: !!lineUid,
    refetchInterval: 30_000,
  })

  const stats = [
    { label: 'ผู้ใช้ทั้งหมด', value: data?.total_users ?? 0, icon: Users, color: '#7a8a6a', bg: '#e8eedc' },
    { label: 'รายได้รวม', value: `${data?.total_revenue_baht ?? 0}฿`, icon: Coins, color: '#b8945a', bg: '#f5e8d0' },
    { label: 'ทำนายฝัน', value: data?.total_dreams ?? 0, icon: Sparkles, color: '#9a7a8e', bg: '#f0e0e8' },
    { label: 'รอตรวจสอบ', value: data?.pending_review ?? 0, icon: AlertCircle, color: '#c9a05f', bg: '#fbecd0', alert: (data?.pending_review ?? 0) > 0 },
  ]

  const maxCount = Math.max(...(data?.daily_dreams_7days ?? []).map(d => d.count), 1)

  return (
    <div className="p-4 md:p-6 lg:p-8">

      {/* ── Hero Welcome Card ──────────────────── */}
      <div className="mb-6 rounded-3xl p-6 md:p-8 relative overflow-hidden"
           style={{
             background: 'linear-gradient(135deg, #d4b884 0%, #b8945a 100%)',
             boxShadow: '0 8px 24px rgba(184, 148, 90, 0.3)',
           }}>
        <div className="relative z-10 flex items-center gap-4 md:gap-6">
          <div className="hidden md:block text-7xl">🪬</div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-white/80 mb-1">
              สวัสดี Admin
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {profile?.display_name ?? 'Admin'} 👋
            </h1>
            <p className="text-sm md:text-base text-white/90 max-w-md">
              วันนี้มีผู้ใช้งาน {data?.active_today ?? 0} คน • ทำนายฝัน {data?.dreams_today ?? 0} ครั้ง
            </p>
          </div>
        </div>

        {/* Decorative blobs */}
        <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full opacity-20"
             style={{ background: '#fff' }} />
        <div className="absolute -right-4 bottom-0 w-32 h-32 rounded-full opacity-10"
             style={{ background: '#fff' }} />
      </div>

      {/* ── Stat Cards ─────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        {stats.map((stat) => (
          <div key={stat.label}
               className="rounded-2xl p-4 md:p-5"
               style={{
                 background: stat.bg,
                 border: stat.alert ? '2px solid #c9a05f' : '1px solid rgba(0,0,0,0.04)',
               }}>
            <div className="flex items-start justify-between mb-2">
              <stat.icon size={20} style={{ color: stat.color }} />
              {stat.alert && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                      style={{ background: '#c9a05f', color: '#fff' }}>
                  ใหม่
                </span>
              )}
            </div>
            <div className="text-2xl md:text-3xl font-bold mb-0.5"
                 style={{ color: '#2d2820' }}>
              {isLoading ? '—' : stat.value}
            </div>
            <div className="text-[11px]" style={{ color: '#6b6354' }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* ── 7-day Trend Chart ───────────────────── */}
      <div className="rounded-2xl p-5 md:p-6 mb-6"
           style={{ background: '#faf7ef', border: '1px solid rgba(0,0,0,0.04)' }}>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={18} style={{ color: '#b8945a' }} />
          <h2 className="font-bold text-base" style={{ color: '#2d2820' }}>
            ทำนายฝัน 7 วันล่าสุด
          </h2>
        </div>

        {isLoading ? (
          <div className="h-32 flex items-center justify-center text-sm"
               style={{ color: '#8a8270' }}>
            กำลังโหลด...
          </div>
        ) : (
          <div className="flex items-end justify-between gap-2 h-32">
            {data?.daily_dreams_7days.map((day) => {
              const heightPercent = (day.count / maxCount) * 100
              const date = new Date(day.date)
              const dayLabel = date.toLocaleDateString('th-TH', { weekday: 'short' })
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1.5">
                  <div className="text-[10px] font-bold" style={{ color: '#b8945a' }}>
                    {day.count}
                  </div>
                  <div className="w-full rounded-t-lg flex items-end"
                       style={{ background: 'rgba(184, 148, 90, 0.1)', height: '100%' }}>
                    <div className="w-full rounded-t-lg transition-all"
                         style={{
                           background: 'linear-gradient(180deg, #d4b884 0%, #b8945a 100%)',
                           height: `${heightPercent}%`,
                           minHeight: day.count > 0 ? '4px' : '0',
                         }} />
                  </div>
                  <div className="text-[10px]" style={{ color: '#8a8270' }}>
                    {dayLabel}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Quick Stats Row ─────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
        <QuickStat label="ธุรกรรมสำเร็จ" value={data?.total_transactions ?? 0} unit="รายการ" emoji="💳" />
        <QuickStat label="Active วันนี้" value={data?.active_today ?? 0} unit="คน" emoji="🟢" />
        <QuickStat label="Admin ทั้งหมด" value={data?.total_admins ?? 0} unit="คน" emoji="👑" />
      </div>
    </div>
  )
}

function QuickStat({ label, value, unit, emoji }: { label: string; value: number; unit: string; emoji: string }) {
  return (
    <div className="rounded-2xl p-4 flex items-center gap-3"
         style={{ background: '#faf7ef', border: '1px solid rgba(0,0,0,0.04)' }}>
      <div className="text-2xl">{emoji}</div>
      <div className="flex-1">
        <div className="text-lg font-bold" style={{ color: '#2d2820' }}>
          {value} <span className="text-xs font-normal" style={{ color: '#8a8270' }}>{unit}</span>
        </div>
        <div className="text-[11px]" style={{ color: '#6b6354' }}>{label}</div>
      </div>
    </div>
  )
}
